// @ts-check

/** @file Loads and uploads the User model's profile-picture attachment. */

import {forcedNonBlankString, optionalNonBlankString} from "typanic"

import {User} from "../frontend-models/user.js"

const PROFILE_PICTURE_ATTACHMENT_NAME = "profilePicture"

/** @typedef {{contentBase64: string, contentType: string, filename: string}} ProfilePictureUpload */

/** @type {Map<string, Promise<string | null>>} */
const profilePictureSourcePromises = new Map()

/**
 * Downloads a user's optional profile picture once per mounted application session.
 * @param {string | number} userId User whose attachment should be downloaded.
 * @returns {Promise<string | null>} Data URL, or null when the optional attachment is absent.
 */
export async function loadUserProfilePictureSource(userId) {
  const cacheKey = String(userId)
  let sourcePromise = profilePictureSourcePromises.get(cacheKey)

  if (!sourcePromise) {
    sourcePromise = downloadUserProfilePictureSource(cacheKey).catch((error) => {
      reportProfilePictureReadFailure(cacheKey, error)
      return null
    })
    profilePictureSourcePromises.set(cacheKey, sourcePromise)
  }

  return await sourcePromise
}

/**
 * Replaces the signed-in user's profile picture through the built-in attachment command.
 * @param {{upload: ProfilePictureUpload, userId: string | number}} args Upload inputs.
 * @returns {Promise<string>} Data URL for the newly attached picture.
 */
export async function attachUserProfilePicture({upload, userId}) {
  const cacheKey = String(userId)
  const contentBase64 = forcedNonBlankString(upload.contentBase64, "profile picture base64 content")
  const contentType = forcedNonBlankString(upload.contentType, "profile picture content type")
  const filename = forcedNonBlankString(upload.filename, "profile picture filename")

  await User.executeCommand("attach", {
    attachment: {
      contentBase64,
      contentType,
      filename
    },
    attachmentName: PROFILE_PICTURE_ATTACHMENT_NAME,
    id: cacheKey
  })

  const source = attachmentDataUrl({contentBase64, contentType})
  profilePictureSourcePromises.set(cacheKey, Promise.resolve(source))
  return source
}

/**
 * Invalidates one user's in-memory picture source.
 * @param {string | number} userId User whose source should be reloaded.
 * @returns {void} Nothing.
 */
export function invalidateUserProfilePictureSource(userId) {
  profilePictureSourcePromises.delete(String(userId))
}

/**
 * Downloads the attachment payload through the generated model transport.
 * @param {string} userId User identifier.
 * @returns {Promise<string | null>} Downloaded data URL or null.
 * @rejects {Error} When transport succeeds with a malformed payload or fails unexpectedly.
 */
async function downloadUserProfilePictureSource(userId) {
  let response

  try {
    response = await User.executeCommand("download", {
      attachmentName: PROFILE_PICTURE_ATTACHMENT_NAME,
      id: userId
    })
  } catch (error) {
    const typedError = /** @type {Error & {errorType?: unknown, velocious?: {code?: unknown}}} */ (error)
    const missingBackingObject = typedError.velocious?.code === "attachment-object-not-found"
    if (typedError.errorType === "record_not_found" && !missingBackingObject) return null
    throw error
  }

  const attachment = response.attachment
  if (!attachment || typeof attachment !== "object" || Array.isArray(attachment)) {
    throw new Error("Profile picture response did not include an attachment")
  }

  const contentBase64 = forcedNonBlankString(attachment.contentBase64, "profile picture response contentBase64")
  const contentType = optionalNonBlankString(attachment.contentType, "profile picture response contentType") || "application/octet-stream"
  return attachmentDataUrl({contentBase64, contentType})
}

/**
 * Builds an image source from validated attachment bytes.
 * @param {{contentBase64: string, contentType: string}} args Attachment content.
 * @returns {string} Browser/native image source containing the bytes.
 */
function attachmentDataUrl({contentBase64, contentType}) {
  return `data:${contentType};base64,${contentBase64}`
}

/**
 * Reports a recoverable optional-avatar read failure once per cached request.
 * @param {string} userId User whose profile picture failed to load.
 * @param {unknown} error Caught profile-picture transport failure.
 * @returns {void} Nothing.
 */
function reportProfilePictureReadFailure(userId, error) {
  const normalizedError = error instanceof Error
    ? error
    : new Error(`Could not load profile picture for user ${userId}: ${String(error)}`)

  if (typeof reportError === "function") {
    try {
      reportError(normalizedError)
    } catch (reportingError) {
      console.warn(`Could not report profile picture failure for user ${userId}`, reportingError)
    }
    return
  }

  console.warn(`Could not load profile picture for user ${userId}`, normalizedError)
}
